/**
 * The painting domain and its coordinate chart.
 *
 * Strokes are stored in *domain coordinates* (a, b) so that a motif survives a change of
 * triangle group: the triangle stretches and the motif stretches with it. The chart is affine in
 * the Klein model (where geodesic triangles are straight-sided), so geodesics stay geodesics:
 *
 *   upper half (Im ≥ 0):  k = a·K_Q + b·K_R
 *   lower half (Im < 0):  k = |a|·conj(K_Q) + b·K_R        (a < 0 encodes the lower half)
 *
 * with k the Klein point of z, K_Q and K_R the Klein points of the vertices Q and R (P = 0).
 * The triangle F is {a ≥ 0, b ≥ 0, a + b ≤ 1}; the rotation domain W = F ∪ m0(F) is
 * {b ≥ 0, |a| + b ≤ 1}.
 */
import type { Complex } from '../geometry/complex';
import { sampleGeodesic } from '../geometry/hyperbolic';
import type { TriangleGroup } from '../geometry/triangle-group';

export interface DomainPoint {
  a: number;
  b: number;
}

/** Disk → domain coordinates. */
export function toDomain(tg: TriangleGroup, z: Complex): DomainPoint {
  const s = 2 / (1 + z.re * z.re + z.im * z.im);
  const kx = z.re * s;
  const ky = z.im * s;
  const a = ky / tg.kleinQ.im;
  const b = (kx - Math.abs(a) * tg.kleinQ.re) / tg.kleinR;
  return { a, b };
}

/** Domain → disk coordinates (clamped just inside the unit disk). */
export function fromDomain(tg: TriangleGroup, d: DomainPoint): Complex {
  let kx = d.b * tg.kleinR + Math.abs(d.a) * tg.kleinQ.re;
  let ky = d.a * tg.kleinQ.im;
  const k2 = kx * kx + ky * ky;
  if (k2 >= 0.999999) {
    const f = Math.sqrt(0.999999 / k2);
    kx *= f;
    ky *= f;
  }
  const den = 1 + Math.sqrt(Math.max(0, 1 - (kx * kx + ky * ky)));
  return { re: kx / den, im: ky / den };
}

/** Scale used to normalise brush widths: Euclidean disk radius of the vertex R. */
export const domainScale = (tg: TriangleGroup): number => tg.R.re;

/** Outline of W (rotation) or F (reflection) as a closed polyline in disk coordinates. */
export function domainOutline(tg: TriangleGroup, rotation: boolean, perSide = 48): Complex[] {
  const { P, Q, R } = tg;
  const Qc = { re: Q.re, im: -Q.im };
  const pts: Complex[] = [];
  const pushSide = (u: Complex, v: Complex) => {
    const s = sampleGeodesic(u, v, perSide);
    for (let i = 0; i < s.length - 1; i++) pts.push(s[i]);
  };
  if (rotation) {
    pushSide(P, Qc);
    pushSide(Qc, R);
    pushSide(R, Q);
    pushSide(Q, P);
  } else {
    pushSide(P, R);
    pushSide(R, Q);
    pushSide(Q, P);
  }
  return pts;
}

export interface Box {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/** Bounding box (disk coordinates) of W, padded by `pad` on every side. */
export function domainBox(tg: TriangleGroup, pad = 0): Box {
  const pts = domainOutline(tg, true, 64);
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const p of pts) {
    x0 = Math.min(x0, p.re);
    x1 = Math.max(x1, p.re);
    y0 = Math.min(y0, p.im);
    y1 = Math.max(y1, p.im);
  }
  return { x0: x0 - pad, y0: y0 - pad, x1: x1 + pad, y1: y1 + pad };
}

/** Is the domain point inside W expanded by `slack` (in domain units)? */
export function nearRotationDomain(d: DomainPoint, slack: number): boolean {
  return d.b >= -slack && Math.abs(d.a) + d.b <= 1 + slack;
}
